/* eslint-disable @typescript-eslint/no-unsafe-return */
import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { FormEventHandler, useCallback, useEffect, useMemo, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  useGetManuscriptsPstemporarylist,
  usePostManuscriptsPstemporarylistExport,
  usePostManuscriptsPstemporarylistImport,
} from '../../../../api/manuscripts/manuscripts';
import { ManuscriptPsTemporaryImportItem } from '../../../../api/model';
import { usePostUtilsDownload } from '../../../../api/utils/utils';
import { useDefaultOptions, useOptionsForBinary } from '../../../../hooks/api';
import yup from '../../../../utils/custom-yup';
import { executeDownload } from '../../../../utils/download';
import ErrorComponent from '../../../atoms/ErrorComponent';
import FileDrop from '../../../atoms/FileDrop';
import WaitComponent from '../../../atoms/WaitComponent';
import BlobErrorModal from '../../../modules/BlobErrorModal';
import ErrorModal from '../../../modules/ErrorModal';
import Modal from '../../../modules/Modal';
import OkModal from '../../../modules/OkModal';
import SpinnerModal from '../../../modules/SpinnerModal';

interface Props {
  productionId: number;
}

/**
 * 原稿タブPS改廃状況一覧
 * @param param0
 * @returns
 */
function ProductionManuscriptsPsSituationList({ productionId }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const options = useDefaultOptions(language);
  const defaultOptions = useOptionsForBinary(language);

  // #region 画面表示
  const {
    data: psTempSituData,
    error: psTempSituError,
    isLoading: psTempSituIsLoading,
    refetch: refechpsTempSitu,
    isFetching: psTempSituIsFetching,
  } = useGetManuscriptsPstemporarylist(productionId, options);

  // 章情報
  const chapterInfo = useMemo(() => {
    if (psTempSituData?.data.chapter_info === undefined) {
      return ['Chap.0', 'Chap.1', 'Chap.2', 'Chap.3', 'Chap.4', 'Chap.5', 'Chap.6', 'Chap.7', 'Chap.8'];
    }
    return psTempSituData.data.chapter_info.map((item) => item.chapter_name ?? '');
  }, [psTempSituData]);

  // 新規カウント
  const newCount = useMemo(() => {
    if (psTempSituData?.data.new_info === undefined) {
      return [0, 0, 0, 0, 0, 0, 0, 0, 0];
    }
    return psTempSituData.data.new_info.map((item) => item.count ?? 0);
  }, [psTempSituData]);

  // 保留カウント
  const pendingCount = useMemo(() => {
    if (psTempSituData?.data.pending_info === undefined) {
      return [0, 0, 0, 0, 0, 0, 0, 0, 0];
    }
    return psTempSituData.data.pending_info.map((item) => item.count ?? 0);
  }, [psTempSituData]);

  // 完了カウント
  const completeCount = useMemo(() => {
    if (psTempSituData?.data.complete_info === undefined) {
      return [0, 0, 0, 0, 0, 0, 0, 0, 0];
    }
    return psTempSituData.data.complete_info.map((item) => item.count ?? 0);
  }, [psTempSituData]);

  // 履歴存在チェック
  const hisDataExists = useMemo(() => {
    if (psTempSituData?.data.import_histores === undefined || psTempSituData?.data.import_histores === null) {
      return [false, false, false, false, false, false, false, false, false];
    }
    const retdata: boolean[] = [];
    for (let c = 0; c < 9; c += 1) {
      retdata.push(psTempSituData?.data.import_histores.find((i) => i.chapter_number_id === c) !== undefined);
    }
    return retdata;
  }, [psTempSituData]);

  // #endregion

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedChapNumId, setSelectedChapNumId] = useState<number>(-1);
  const onHisClick = useCallback((chapterNumId: number) => {
    setSelectedChapNumId(chapterNumId);
    setDialogOpen(true);
  }, []);

  const [impDialogOpen, setImpDialogOpen] = useState(false);
  const onImpClick = useCallback((chapterNumId: number) => {
    setSelectedChapNumId(chapterNumId);
    setImpDialogOpen(true);
  }, []);

  // #region PS改廃エクスポート
  const [displayPsTempSituExError, setDisplayPsTempSituExError] = useState(false);
  const {
    mutateAsync: psTempSituExAsync,
    error: psTempSituExError,
    isLoading: psTempSituExIsLoading,
  } = usePostManuscriptsPstemporarylistExport(options);
  useEffect(() => setDisplayPsTempSituExError(psTempSituExError != null), [psTempSituExError]);

  // ダウンロードAPI
  const [displayDownLoadError, setDisplayDownLoadError] = useState(false); // ダウンロードエラー
  const [displayMessage, setDisplayMessage] = useState('');
  const { mutateAsync: downloadAsync, error: downloadError } = usePostUtilsDownload<AxiosError<Blob>>(defaultOptions);
  useEffect(() => {
    setDisplayDownLoadError(downloadError != null);
  }, [downloadError]);

  // 出力リスト
  const onExpClick = useCallback(
    async (chapterNumberId: number) => {
      const response = await psTempSituExAsync({ productionId, chapterNumberId });
      if (response.status === 200) {
        // 処理が成功したら結果をダウンロード
        if (response.data.message !== '') {
          setDisplayMessage(response.data.message ?? '');
        } else if (response.data.result_file_info !== undefined && response.data.result_file_info !== null) {
          await downloadAsync(
            { data: response.data.result_file_info },
            {
              onSuccess: (responseDL) => {
                executeDownload(responseDL);
              },
            }
          );
        }
      }
    },
    [psTempSituExAsync, downloadAsync, productionId]
  );

  // #endregion

  if (psTempSituIsLoading) {
    return <WaitComponent />;
  }
  if (psTempSituError != null) {
    return <ErrorComponent error={psTempSituError} />;
  }

  return (
    <div className="manuscript ">
      <table className="tbl-manu-ps">
        <thead>
          <tr>
            <th>
              <span className="text-button" onClick={() => refechpsTempSitu()}>
                <i className="fa-solid fa-arrows-rotate" />
              </span>
            </th>
            {chapterInfo.map((item, index) => (
              <th key={`${String(index)}-${String(item)}`}>{t(item)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{t('新規')}</td>
            {newCount.map((item, index) => (
              <td key={`${String(index)}-${String(item)}`}>{item}</td>
            ))}
          </tr>
          <tr>
            <td>{t('保留')}</td>
            {pendingCount.map((item, index) => (
              <td key={`${String(index)}-${String(item)}`}>{item}</td>
            ))}
          </tr>
          <tr>
            <td>{t('完了')}</td>
            {completeCount.map((item, index) => (
              <td key={`${String(index)}-${String(item)}`}>{item}</td>
            ))}
          </tr>
          <tr>
            <td>{t('Imp./Exp.')}</td>
            {chapterInfo.map((item, index) => (
              <td key={`${String(index)}-${String(item)}`}>
                <div style={{ marginBottom: '4px' }}>
                  <span className="span-fa-enable" onClick={() => onImpClick(index)}>
                    <i className="fa-solid fa-upload" />
                    {t(`Imp.${index}`)}
                  </span>
                </div>
                <div>
                  <span className="span-fa-enable" onClick={() => onExpClick(index)}>
                    <i className="fa-solid fa-download" />
                    {t(`Exp.${index}`)}
                  </span>
                </div>
              </td>
            ))}
          </tr>
          <tr>
            <td>{t('履歴')}</td>
            {hisDataExists.map((item, index) => (
              <td key={`${String(index)}`}>
                <div>
                  <span
                    className={item ? 'span-fa-enable' : 'span-fa-disable'}
                    onClick={() => item && onHisClick(index)}
                  >
                    <i className="fa-solid fa-history" />
                    {t('履歴')}
                  </span>
                </div>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
      {dialogOpen && (
        <ImportHistoryDialog
          chapterNumberId={selectedChapNumId}
          importHistores={psTempSituData?.data.import_histores}
          isOpen={dialogOpen}
          setIsOpen={setDialogOpen}
        />
      )}
      {impDialogOpen && (
        <ImportDialog
          productionId={productionId}
          chapterNumberId={selectedChapNumId}
          isOpen={impDialogOpen}
          setIsOpen={setImpDialogOpen}
          refechpsTempSitu={refechpsTempSitu}
        />
      )}

      {/* スピナーモーダルの表示 */}
      {psTempSituIsFetching && <SpinnerModal />}
      {psTempSituExIsLoading && <SpinnerModal />}
      {/* 一括置換リスト作成時の例外エラー  */}
      {displayPsTempSituExError && (
        <ErrorModal error={psTempSituExError} onClickOk={() => setDisplayPsTempSituExError(false)} />
      )}
      {/* 一括置換リストメッセージ */}
      {displayMessage !== '' && <OkModal onClickOk={() => setDisplayMessage('')}>{displayMessage}</OkModal>}
      {/* ダウンロード時のエラー  */}
      {displayDownLoadError && (
        <BlobErrorModal error={downloadError} onClickOk={() => setDisplayDownLoadError(false)} />
      )}
    </div>
  );
}

export default ProductionManuscriptsPsSituationList;

// #region 履歴ダイアログ
interface ImportHistoryDialogProps {
  /** 章番号ID */
  chapterNumberId: number;
  /** 履歴情報 */
  importHistores: ManuscriptPsTemporaryImportItem[] | undefined;
  /** モーダル表示・非表示状態。 */
  isOpen: boolean;
  /** モーダル表示・非表示状態設定関数。 */
  setIsOpen: (isOpen: boolean) => void;
}

/** 履歴ダイアログ */
function ImportHistoryDialog({ chapterNumberId, importHistores, isOpen, setIsOpen }: ImportHistoryDialogProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const defaultOptions = useOptionsForBinary(language);

  /** ダイアログを閉じる */
  const onClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const downLoadInfo = useMemo(() => {
    if (importHistores !== undefined) {
      const result = importHistores.find((i) => i.chapter_number_id === chapterNumberId);
      if (result !== undefined) {
        return result.file_info;
      }
    }
    return undefined;
  }, [importHistores, chapterNumberId]);

  const historyData = useMemo(() => {
    if (importHistores !== undefined) {
      const result = importHistores.filter((i) => i.chapter_number_id === chapterNumberId);
      if (result !== undefined) {
        return result ?? undefined;
      }
    }
    return undefined;
  }, [importHistores, chapterNumberId]);

  // ダウンロードAPI
  const { mutateAsync: downloadAsync } = usePostUtilsDownload<AxiosError<Blob>>(defaultOptions);

  /** ファイルダウンロード */
  const onFileDownLoad = useCallback(async () => {
    if (downLoadInfo !== undefined) {
      await downloadAsync({ data: downLoadInfo }, { onSuccess: (responseDL) => executeDownload(responseDL) });
    }
  }, [downloadAsync, downLoadInfo]);

  return (
    <Modal active={isOpen} additionalClassName="popupfr800" displayCloseButton onClose={() => onClose()}>
      {historyData === undefined ? (
        <p>アップロードされたファイルはありません。</p>
      ) : (
        <div className="tableWrapper60">
          <table className="table60">
            <thead>
              <tr>
                <th>{t('ファイル名')}</th>
                <th>{t('インポート日時')}</th>
                <th>{t('ユーザー')}</th>
              </tr>
            </thead>
            <tbody>
              {historyData.map((item) => (
                <tr key={item.ps_temporary_situation_file_id}>
                  <td>
                    <p className="downloadP" onClick={() => onFileDownLoad()}>
                      {item.file_name}
                    </p>
                  </td>
                  <td>{item.import_at}</td>
                  <td>{item.import_user}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
// #endregion

// #region インポートダイアログ
interface ImportDialogProps {
  /** 資料管理ID */
  productionId: number;
  /** 章番号ID */
  chapterNumberId: number;
  /** モーダル表示・非表示状態。 */
  isOpen: boolean;
  /** モーダル表示・非表示状態設定関数。 */
  setIsOpen: (isOpen: boolean) => void;
  /** 一覧更新 */
  refechpsTempSitu: () => void;
}

/** インポートダイアログ */
function ImportDialog({ productionId, chapterNumberId, isOpen, setIsOpen, refechpsTempSitu }: ImportDialogProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // Form定義
  const schema = yup.object({
    comment: yup
      .string()
      .nullable()
      .transform((v) => (v ? (v as string) : undefined)),
    importFiles: yup.array().required(),
  });

  const {
    handleSubmit,
    setValue,
    register,
    reset,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [displayOkModal, setDisplayOkModal] = useState(0);

  const onClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const options = useDefaultOptions(language);
  // インポート処理API
  const [displayImportError, setDisplayImportError] = useState(false);
  const {
    mutateAsync: importAsync,
    error: importError,
    isLoading: importIsLoading,
  } = usePostManuscriptsPstemporarylistImport(options);
  useEffect(() => setDisplayImportError(importError != null), [importError]); // インポート処理エラー

  // インポートエラーログダウンロードAPI
  const optionsForBinary = useOptionsForBinary(language);
  const { mutateAsync: downloadAsync } = usePostUtilsDownload<AxiosError<Blob>>(optionsForBinary);

  /** インポート実行 */
  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      setIsSubmitting(true);

      void handleSubmit(async (data: FieldValues) => {
        if (data.importFiles.length > 0) {
          const responseImport = await importAsync({
            productionId,
            chapterNumberId,
            data: { file: data.importFiles?.[0] },
          });

          if (responseImport.status === 200) {
            // apiの返値にエラーログのパスが返って来たらダウンロードする
            if (responseImport.data.file_info !== undefined && responseImport.data.file_info !== null) {
              setDisplayOkModal(2);
              await downloadAsync(
                { data: responseImport.data.file_info },
                { onSuccess: (responseDL) => executeDownload(responseDL) }
              );
            } else {
              setDisplayOkModal(1);
              void refechpsTempSitu(); // 一覧再読込み
            }
          }
        }
      })();
    },
    [handleSubmit, importAsync, downloadAsync, productionId, chapterNumberId, refechpsTempSitu]
  );
  return (
    <Modal active={isOpen} additionalClassName="popupfr800" displayCloseButton onClose={() => onClose()}>
      <form onSubmit={onSubmit}>
        <fieldset disabled={isSubmitting} className="fieldset-reset">
          <div className="formarea1">
            <label htmlFor="">{t('添付ファイル')}</label>
            <div>
              <FileDrop id="importFiles" name="importFiles" setValue={setValue} />
              {errors.importFiles?.message != null && (
                <p className="form-error">{errors.importFiles.message.toString()}</p>
              )}
            </div>
          </div>
          <div className="btnfr btnfr2 mb0">
            <div>
              <button type="button" className="text-button" onClick={() => onClose()} style={{ marginRight: '5px' }}>
                <span className="btn2 mr10 btnbg-g">
                  <span className="modal-close2">{t('キャンセル')}</span>
                </span>
              </button>
              <span>
                <input type="submit" id="" name="" value={t('インポート').toString()} className="btn1" />
              </span>{' '}
            </div>
          </div>
        </fieldset>
      </form>
      {/* スピナーモーダル */}
      {importIsLoading && <SpinnerModal />}
      {/* インポートエラー */}
      {displayImportError && (
        <ErrorModal
          error={importError}
          onClickOk={() => {
            setDisplayImportError(false);
            setIsOpen(false);
          }}
        />
      )}
      {/* インポート完了 */}
      {displayOkModal > 0 && (
        <OkModal
          onClickOk={() => {
            setDisplayOkModal(0);
            setIsOpen(false);
          }}
        >
          <div>
            {displayOkModal === 1 && t('インポート処理が完了しました。')}
            {displayOkModal === 2 && (
              <>
                {t('インポート処理が完了しましたが、一部インポートできないデータがありました。')}
                <br />
                {t('出力されたログを確認してください。')}
              </>
            )}
          </div>
        </OkModal>
      )}
    </Modal>
  );
}
// #endregion
